import { takeLatest, put, call, all, select } from 'redux-saga/effects';

import { User } from '../reducers/user';

import {
  GET_USER_DATA_REQUEST,
  USER_LOGIN_REQUEST,
  USER_UPDATE_REQUEST,
  USER_TOKEN_LOGIN_REQUEST,
} from '../actions/constants';
import {
  setUserData,
  userSignInError,
  signInSuccess,
  signInRequest,
  tokenLoginRequest,
  userUpdateRequest,
  userUpdateFailure,
} from '../actions/user';
import { showMessage } from '../actions/message';
import { getUser } from '../selectors/user';

import { booleanToString, stringToBoolean } from '../utils';
import { api } from '../utils/api';

const signIn = async (method: 'get' | 'post', url: string, body = {}) => {
  const { data }: { data: User } = await api[method](url, {}, body);

  if (!data) {
    throw new Error('An error happened while signing user in');
  }

  return data;
};

type APIUserSchema = {
  analyticsCheck: '0' | '1';
  cap: string | null;
  citta: string | null;
  civico: string | null;
  cognome: string | null;
  data: string | null;
  data_inserimento: string;
  data_nascita: string | null;
  email: string;
  fiscal_code: string;
  id_contactlab: string | null;
  id_utente: string;
  indirizzo: string | null;
  lang: string | null;
  marketingCheck: '0' | '1';
  mobile: string | null;
  newsletter: '0' | '1';
  nome: string | null;
  novita: '0' | '1';
  nucleo_familiare: string | null;
  password: string;
  privacy: '0' | '1';
  provincia: string | null;
  reset_token: string | null;
  sesso: 'm' | 'f';
  source: string;
  token: string;
};

function* userSignIn(
  url: string,
  action: 'get' | 'post',
  errorMessage: string,
  payload?: any
) {
  try {
    const data: APIUserSchema = yield call(signIn, action, url, payload);

    yield put(
      setUserData({
        address: data.indirizzo || '',
        analyticsChecked: stringToBoolean(data.analyticsCheck),
        birthday: data.data_nascita || '',
        city: data.citta || '',
        data: (data.data && JSON.parse(data.data)) || [],
        email: data.email,
        fiscalCode: data.fiscal_code,
        houseNumber: data.civico || '',
        marketingChecked: stringToBoolean(data.marketingCheck),
        id: data.id_utente,
        mobile: data.mobile || '',
        name: data.nome || '',
        newsletterChecked: stringToBoolean(data.newsletter),
        password: data.password,
        postalCode: data.cap || '',
        privacyChecked: data.privacy,
        province: data.provincia || '',
        sex: data.sesso,
        surname: data.cognome || '',
        token: data.token,
      })
    );

    yield put(signInSuccess());
  } catch {
    yield put(userSignInError());
    yield put(
      showMessage({
        message: errorMessage,
        type: 'danger',
      })
    );
  }
}

export function* signInUser(action: ReturnType<typeof signInRequest>) {
  yield userSignIn(
    '/login',
    'post',
    'E-mail e/o password errati!',
    action.payload
  );
}

function* tokenSignIn(action: ReturnType<typeof tokenLoginRequest>) {
  yield userSignIn(
    `/user/token_login/${action.payload.token}`,
    'get',
    "L'utente ha già una password oppure non è ancora stato creato"
  );
}

const payloadKeys = {
  address: 'indirizzo',
  analyticsChecked: 'analyticsCheck',
  birthday: 'data_nascita',
  city: 'citta',
  data: 'data',
  email: 'email',
  fiscalCode: 'fiscal_code',
  houseNumber: 'civico',
  marketingChecked: 'marketingCheck',
  id: 'id_utente',
  mobile: 'mobile',
  name: 'nome',
  newsletterChecked: 'newsletter',
  password: 'password',
  postalCode: 'cap',
  privacyChecked: 'privacy',
  province: 'provincia',
  sex: 'sesso',
  surname: 'cognome',
} as const;

export function* updateUserSaga(action: ReturnType<typeof userUpdateRequest>) {
  try {
    const user: ReturnType<typeof getUser> = yield select(getUser);
    const { token, ...payload } = action.payload;
    const update = Object.entries(payload).reduce<Partial<APIUserSchema>>(
      (accumulator, [key, value]) => {
        const payloadKey = payloadKeys[key as keyof typeof payloadKeys];

        if (typeof value === 'string') {
          return {
            ...accumulator,
            [payloadKey]: value,
          };
        }

        if (typeof value === 'boolean') {
          return {
            ...accumulator,
            [payloadKey]: booleanToString(value),
          };
        }

        if (Array.isArray(value)) {
          return {
            ...accumulator,
            [payloadKey]: JSON.stringify(value),
          };
        }

        return accumulator;
      },
      { email: user.data!.email }
    );

    yield call(
      [api, api.post],
      `/user/${user.data!.id}`,
      {
        Authorization: `Bearer ${user.data!.token}`,
      },
      update
    );

    yield all([
      put(
        setUserData({
          ...user.data!,
          ...payload,
        })
      ),
      put(
        showMessage({
          message: 'I tuoi dati sono stati salvati correttamente.',
        })
      ),
    ]);
  } catch {
    yield put(userUpdateFailure());
    yield put(
      showMessage({
        message: "Si è verificato un errore durante l'aggiornamento dei dati",
        type: 'danger',
      })
    );
  }
}

function* getUserData() {
  const user: ReturnType<typeof getUser> = yield select(getUser);

  try {
    const { data } = yield call([api, api.get], `/user/${user.data!.id}`, {
      Authorization: `Bearer ${user.data!.token}`,
    });

    yield put(
      setUserData({
        ...user.data!,
        address: data.indirizzo || '',
        analyticsChecked: stringToBoolean(data.analyticsCheck),
        birthday: data.data_nascita || '',
        city: data.citta || '',
        data: (data.data && JSON.parse(data.data)) || [],
        email: data.email,
        fiscalCode: data.fiscal_code,
        houseNumber: data.civico || '',
        marketingChecked: stringToBoolean(data.marketingCheck),
        id: data.id_utente,
        mobile: data.mobile || '',
        name: data.nome || '',
        newsletterChecked: stringToBoolean(data.newsletter),
        password: data.password,
        postalCode: data.cap || '',
        privacyChecked: data.privacy,
        province: data.provincia || '',
        sex: data.sesso,
        surname: data.cognome || '',
      })
    );
  } catch {
    yield put(
      showMessage({
        message: 'Si è verificato un errore durante la ricerca dei dati',
        type: 'danger',
      })
    );
  }
}

export function* userSaga() {
  yield all([
    takeLatest(USER_UPDATE_REQUEST, updateUserSaga),
    takeLatest(USER_LOGIN_REQUEST, signInUser),
    takeLatest(USER_TOKEN_LOGIN_REQUEST, tokenSignIn),
    takeLatest(GET_USER_DATA_REQUEST, getUserData),
  ]);
}
